/**
 * @file product-kind-form.ts
 * @description Модель формы вида товара (phase19.22, W22): значение формы,
 * заполнение из записи, клиентская проверка и сборка тела запроса.
 *
 * Формой владеет `ProductKindForm`, поля рисует `ProductKindFormFields` по
 * общему контракту `value/onChange`. Числа в форме — строками (как ввёл
 * человек), в запрос уходят числами или `null`.
 *
 * Правило сервера «срок по умолчанию только у вида, требующего срок» (422
 * `product_kind_shelf_life_without_expiry`) держим и здесь: снятый
 * переключатель отправляет `defaultShelfLifeDays: null`.
 */

import type {
  LabelTemplate,
  LabelingRegulation,
  ProductKind,
  ProductKindInput,
} from "../../../lib/api-product-kinds";
import { REGULATION_NOTE_MAX } from "../../../lib/product-kind-format";

export interface ProductKindFormValue {
  name: string;
  requiresExpiry: boolean;
  /** Дни строкой; "" — не задан. */
  defaultShelfLifeDays: string;
  labelTemplate: LabelTemplate;
  labelingRegulation: LabelingRegulation;
  /** Название «другого» регламента; нужно только при `OTHER`. */
  labelingRegulationNote: string;
  /** Проценты строкой; "" — проставляется вручную. */
  defaultVatRate: string;
  returnToStockAllowed: boolean;
  description: string;
}

export type ProductKindFormErrors = Partial<Record<keyof ProductKindFormValue, string>>;

/** Границы срока — как CHECK в базе (1..3660 дней). */
const SHELF_MIN = 1;
const SHELF_MAX = 3660;

/** Новый вид: срок требуется, этикетка «Стандарт» — как значения по умолчанию сервера. */
export const EMPTY_KIND_FORM: ProductKindFormValue = {
  name: "",
  requiresExpiry: true,
  defaultShelfLifeDays: "",
  labelTemplate: "STANDARD",
  labelingRegulation: "NONE",
  labelingRegulationNote: "",
  defaultVatRate: "",
  returnToStockAllowed: false,
  description: "",
};

export function kindFormFromRecord(kind: ProductKind | null): ProductKindFormValue {
  if (!kind) return { ...EMPTY_KIND_FORM };
  return {
    name: kind.name,
    requiresExpiry: kind.requiresExpiry,
    defaultShelfLifeDays: kind.defaultShelfLifeDays?.toString() ?? "",
    labelTemplate: kind.labelTemplate,
    labelingRegulation: kind.labelingRegulation,
    labelingRegulationNote: kind.labelingRegulationNote ?? "",
    defaultVatRate: kind.defaultVatRate?.toString() ?? "",
    returnToStockAllowed: kind.returnToStockAllowed,
    description: kind.description ?? "",
  };
}

/** Проверка до отправки: пустой объект — можно сохранять. */
export function validateKindForm(value: ProductKindFormValue): ProductKindFormErrors {
  const errors: ProductKindFormErrors = {};
  const name = value.name.trim();
  if (!name) errors.name = "Введите название вида.";
  else if (name.length > 80) errors.name = "Не длиннее 80 символов.";

  if (value.requiresExpiry && value.defaultShelfLifeDays.trim()) {
    const days = Number(value.defaultShelfLifeDays);
    if (!Number.isInteger(days) || days < SHELF_MIN || days > SHELF_MAX) {
      errors.defaultShelfLifeDays = `Целое число дней от ${SHELF_MIN} до ${SHELF_MAX}.`;
    }
  }
  if (value.labelingRegulation === "OTHER") {
    const note = value.labelingRegulationNote.trim();
    if (!note) errors.labelingRegulationNote = "Укажите название регламента.";
    else if (note.length > REGULATION_NOTE_MAX) {
      errors.labelingRegulationNote = `Не длиннее ${REGULATION_NOTE_MAX} символов.`;
    }
  }
  if (value.description.length > 2000) {
    errors.description = "Не длиннее 2000 символов.";
  }
  return errors;
}

/** Тело POST/PATCH: все поля явно, `null` — сброс значения. */
export function kindFormToInput(value: ProductKindFormValue): ProductKindInput {
  const shelf = value.defaultShelfLifeDays.trim();
  return {
    name: value.name.trim(),
    requiresExpiry: value.requiresExpiry,
    defaultShelfLifeDays: value.requiresExpiry && shelf ? Number(shelf) : null,
    labelTemplate: value.labelTemplate,
    labelingRegulation: value.labelingRegulation,
    // Название храним только у «Другого»: у остальных сервер ждёт null.
    labelingRegulationNote:
      value.labelingRegulation === "OTHER" ? value.labelingRegulationNote.trim() : null,
    defaultVatRate: value.defaultVatRate === "" ? null : Number(value.defaultVatRate),
    returnToStockAllowed: value.returnToStockAllowed,
    description: value.description.trim() || null,
  };
}

/**
 * Ошибка сервера → поле формы. Код не из реестра — `null`: форма покажет
 * текст сервера общей строкой.
 */
export const KIND_ERROR_FIELD: Record<string, keyof ProductKindFormValue> = {
  product_kind_name_taken: "name",
  product_kind_shelf_life_without_expiry: "defaultShelfLifeDays",
  labeling_regulation_note_required: "labelingRegulationNote",
};

/** Человеческие тексты для ошибок, привязанных к полю. */
export const KIND_ERROR_TEXT: Record<string, string> = {
  product_kind_name_taken: "Вид с таким названием уже есть.",
  product_kind_shelf_life_without_expiry:
    "Срок по умолчанию задаётся только виду, которому нужен срок годности.",
  labeling_regulation_note_required: "Укажите название регламента.",
};
